import { createContext } from 'react';
import type { SupportedLocale } from '@/i18n/localeConfigs';
import type { AuthUser } from '@/types/auth';

export type AppShellLanguage = SupportedLocale;

export type AppShellTheme = 'light' | 'dark' | 'system';

export type AppShellResolvedTheme = 'light' | 'dark';

export type ThemeStudioDensity = 'compact' | 'comfortable' | 'spacious';

export type ThemeStudioSidebarStyle =
  'default' | 'compact' | 'floating' | 'collapsed';

export type ThemeStudioFontFamily = 'inter' | 'segoe' | 'roboto' | 'system';

export type ThemeStudioFontScale = 'small' | 'medium' | 'large';

export interface ThemeStudioPreferences {
  mode: AppShellTheme;
  themeId: string;
  accentColor: string;
  layoutDensity: ThemeStudioDensity;
  sidebarStyle: ThemeStudioSidebarStyle;
  roundedCorners: boolean;
  animations: boolean;
  reducedMotion: boolean;
  denseTables: boolean;
  stickyHeaders: boolean;
  fontFamily: ThemeStudioFontFamily;
  fontScale: ThemeStudioFontScale;
  recentColors: string[];
}

export type AppShellEnvironment = 'production' | 'stage' | 'test' | 'local';

export type AppShellUserStatus = 'online' | 'away' | 'offline';

export interface AppShellUserProfile {
  id: string;
  displayName: string;
  email: string;
  avatar?: string;
  roles: string[];
  permissions: string[];
  preferredLanguage: AppShellLanguage;
  theme: AppShellTheme;
  environment: AppShellEnvironment;
  status: AppShellUserStatus;
  lastLogin?: string;
}

export interface AppShellContextValue {
  userProfile: AppShellUserProfile | null;
  isUserProfileLoading: boolean;
  userProfileError: string | null;
  language: AppShellLanguage;
  theme: AppShellTheme;
  resolvedTheme: AppShellResolvedTheme;
  themeStudioPreferences: ThemeStudioPreferences;
  environment: AppShellEnvironment;
  currentProductId: string | null;
  currentEntityId: string | null;
  breadcrumbs: Array<{ label: string; href?: string }>;
  profileMenuAnchorEl: HTMLElement | null;
  isProfileMenuOpen: boolean;
  setLanguage: (language: AppShellLanguage) => Promise<void>;
  setTheme: (theme: AppShellTheme) => Promise<void>;
  setThemeStudioPreferences: (updates: Partial<ThemeStudioPreferences>) => void;
  replaceThemeStudioPreferences: (preferences: ThemeStudioPreferences) => void;
  saveThemeStudioPreferences: () => Promise<void>;
  resetThemeStudioPreferences: () => Promise<void>;
  setEnvironment: (environment: AppShellEnvironment) => Promise<void>;
  onEnvironmentChanged: (environment: AppShellEnvironment) => Promise<void>;
  setCurrentProductId: (productId: string | null) => void;
  setCurrentEntityId: (entityId: string | null) => void;
  setBreadcrumbs: (
    breadcrumbs: Array<{ label: string; href?: string }>,
  ) => void;
  openProfileMenu: (anchorEl: HTMLElement) => void;
  closeProfileMenu: () => void;
  refreshUserProfile: (authUser?: AuthUser | null) => Promise<void>;
  retryLoadUserProfile: (authUser?: AuthUser | null) => Promise<void>;
  hasPermission: (permission: string) => boolean;
  setAuthenticatedUser: (user: AuthUser | null) => void;
}

export const AppShellContext = createContext<AppShellContextValue | undefined>(
  undefined,
);
